import React, { forwardRef, useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';

export const Checkbox = forwardRef(function Checkbox(
  { isIndeterminate = false, label, description, isDisabled, isInvalid, className, ...rest },
  forwardedRef
) {
  const innerRef = useRef(null);
  const ref = forwardedRef || innerRef;
  useEffect(() => {
    if (ref?.current) ref.current.indeterminate = isIndeterminate;
  }, [isIndeterminate, ref]);

  return (
    <label className={cn('inline-flex items-start gap-2.5 cursor-pointer select-none', isDisabled && 'opacity-50 cursor-not-allowed', className)}>
      <span className="relative shrink-0 mt-0.5">
        <input
          ref={ref}
          type="checkbox"
          disabled={isDisabled}
          aria-invalid={isInvalid || undefined}
          className={cn(
            'peer w-4 h-4 rounded-sm appearance-none border bg-surface',
            'border-border-strong transition-colors',
            'checked:bg-ink checked:border-ink',
            'indeterminate:bg-ink indeterminate:border-ink',
            'focus-visible:ring-2 focus-visible:ring-ink/30 focus-visible:ring-offset-1 focus-visible:ring-offset-bg outline-none',
            'disabled:cursor-not-allowed',
            isInvalid && 'border-danger',
          )}
          {...rest}
        />
        <Check className="w-3 h-3 absolute top-0.5 left-0.5 text-cream opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
        {isIndeterminate && <Minus className="w-3 h-3 absolute top-0.5 left-0.5 text-cream pointer-events-none" strokeWidth={3} />}
      </span>
      {(label || description) && (
        <span className="leading-tight">
          {label && <span className="block text-sm text-text">{label}</span>}
          {description && <span className="block text-xs text-mute mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});

export function CheckboxGroup({ legend, children, orientation = 'vertical', className }) {
  return (
    <fieldset className={cn(className)}>
      {legend && <legend className="text-sm font-semibold text-text mb-2">{legend}</legend>}
      <div className={cn('flex gap-3', orientation === 'vertical' ? 'flex-col' : 'flex-row')}>
        {children}
      </div>
    </fieldset>
  );
}

Checkbox.Group = CheckboxGroup;
export default Checkbox;
